/**
 * PUTTING THE PAGE ON ITS OWN ADDRESS
 * ===================================
 *
 * An event can be reached two ways. `…/events/details/?id=337489` is what a
 * link out of MP, a calendar file or the finder hands you; `…/events/flannel-
 * fest/` is what staff want on a flyer and what anybody would want to send a
 * friend. Both load the same page.
 *
 * Once the event has answered, the page knows its slug, so the ugly address
 * can quietly become the pretty one. `history.replaceState` rather than
 * `pushState`: the two URLs are the same page, so putting a step between
 * them would mean Back appears to do nothing.
 *
 * Only ever within the same origin — a widget embedded somewhere else must
 * not rewrite that site's address bar to woodsidebible.org — and only when
 * the result would actually be a different address.
 */

/**
 * Where an event with this slug lives, given where the finder lives.
 *
 * The slug hangs off the events index: `https://woodsidebible.org/events/`
 * plus `flannel-fest` is `https://woodsidebible.org/events/flannel-fest/`.
 * That is the WordPress routing that already serves these pages, so no new
 * configuration is invented for it.
 *
 * Returns null when the two cannot be combined, which is every case where
 * guessing would be worse than leaving the URL alone.
 */
export function prettyUrlFor(
  slug: string | null | undefined,
  finderUrl: string | null | undefined,
  here: string
): string | null {
  if (!slug || !finderUrl) return null;
  // The same shape the API validates: hyphenated words, in up to three
  // `/`-joined segments, so `baptism/lake-orion` survives the rewrite.
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[a-z0-9]+(?:-[a-z0-9]+)*){0,2}$/.test(slug)) return null;
  try {
    const current = new URL(here);
    // Mid-flow, the query string is not decoration. An invoice guid or an
    // OLG token is how this page knows which registration it is looking at,
    // and a refresh after we had thrown it away would land somewhere else.
    // Those visits keep the address they arrived on.
    for (const key of ['invoice', 'token']) if (current.searchParams.has(key)) return null;
    const base = new URL(finderUrl, current);
    // Somebody else's site is not ours to rewrite.
    if (base.origin !== current.origin) return null;
    const path = base.pathname.endsWith('/') ? base.pathname : `${base.pathname}/`;
    const pretty = new URL(`${path}${slug}/`, current);
    // Except the time machine's own params (experiences/kit/preview.ts): a
    // staffer previewing the recap should still be in it after a refresh.
    for (const key of ['stage', 'at', 'as', 'xp', 'tm']) {
      const v = current.searchParams.get(key);
      // `tm` is a bare flag (?tm), so presence counts, not a value.
      if (v || (key === 'tm' && current.searchParams.has('tm')))
        pretty.searchParams.set(key, v ?? '');
    }
    // Carry nothing across: the query string is how we got here (`?id=`, an
    // invoice guid, OLG's token) and none of it belongs on the shareable
    // address. That is the whole point of the swap.
    if (pretty.pathname === current.pathname && pretty.search === current.search) return null;
    return pretty.toString();
  } catch {
    return null;
  }
}

/**
 * WHAT THE TAB SHOULD SAY.
 *
 * The WordPress page is one template serving every event, so its title is
 * whatever was typed once — "Event - Woodside Bible" — on the flannel-fest
 * tab and on every other. A person with six tabs open cannot tell which is
 * which, and a bookmark saves the generic name.
 *
 * The event's own name goes in front. The site's half of the title is kept
 * rather than assumed: whatever follows the LAST separator is the site saying
 * who it is, and that is not this widget's to rewrite — a church that is not
 * Woodside gets its own suffix for free, and a page with no separator simply
 * gets the event name.
 *
 * THE LAST SEPARATOR, NOT THE FIRST, and it matters. Plenty of events are
 * named like "Faithful Friends | Senior Adults | Troy", so an event name can
 * carry more separators than the site does. Splicing at the first one wrote
 * the name in on top of itself: "Faithful Friends | Senior Adults | Troy |
 * Senior Adults | Troy - Woodside Bible".
 *
 * AND USUALLY THERE IS NOTHING TO DO AT ALL. WordPress now names these pages
 * server-side, so that a crawler reading the raw HTML gets the event rather
 * than the template, which means the tab is already right before this runs.
 * A title that already opens with the event's name is left alone.
 *
 * @param eventTitle the event's public name
 * @param currentTitle whatever the page is called right now
 */
export function pageTitleFor(eventTitle: string, currentTitle: string): string | null {
  const name = eventTitle.trim();
  if (!name) return null;
  // Already said, by the server or by a previous run of this.
  if (currentTitle === name || currentTitle.startsWith(`${name} `)) return null;
  // The separators a WordPress title actually uses. Spaces on both sides, so
  // a hyphenated word is not mistaken for a divider.
  const separator = /\s+[|\u2013\u2014-]\s+/g;
  let last: RegExpExecArray | null = null;
  for (let m = separator.exec(currentTitle); m; m = separator.exec(currentTitle)) last = m;
  const next = last ? `${name}${currentTitle.slice(last.index)}` : name;
  return next === currentTitle ? null : next;
}
